import { useEffect, useState } from "react";
import { Modal, Button as BsButton, Form } from "react-bootstrap";
import { InfoCircleFill } from "react-bootstrap-icons";
import { LocalDate } from "@js-joda/core";

interface CancelParkerModalProps {
  show: boolean;
  onHide: () => void;
  onConfirm: (cancellationDate: string) => void;
  minCancellationDate?: string;
  defaultCancellationDate?: string;
  /** Shown for staged changes (e.g. group parkers page) that require save. */
  showPendingSaveNote?: boolean;
}

export function CancelParkerModal({
  show,
  onHide,
  onConfirm,
  minCancellationDate,
  defaultCancellationDate,
  showPendingSaveNote = false,
}: CancelParkerModalProps) {
  const minDate = minCancellationDate ?? LocalDate.now().toString();
  const defaultDate = defaultCancellationDate ?? minDate;
  const [cancellationDate, setCancellationDate] = useState(defaultDate);
  const [cancelImmediately, setCancelImmediately] = useState(false);

  useEffect(() => {
    if (show) {
      setCancellationDate(defaultDate);
      setCancelImmediately(false);
    }
  }, [show, defaultDate]);

  return (
    <Modal centered show={show} onHide={onHide}>
      <Modal.Header closeButton>
        <Modal.Title>Cancel Parker</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="mb-3">Are you sure you want to cancel this parker?</p>
        <div className="alert alert-muted-gold d-flex align-items-start gap-2 mb-3">
          <InfoCircleFill className="flex-shrink-0 mt-1" />
          <span>
            Access continues through the end of the cancellation date.
            Any applicable credits must be issued manually.
          </span>
        </div>
        <Form.Check
          className="mb-3"
          type="checkbox"
          id="cancel-parker-immediately"
          label="Cancel immediately"
          checked={cancelImmediately}
          onChange={(e) => {
            const isChecked = e.target.checked;
            setCancelImmediately(isChecked);
            setCancellationDate(isChecked ? minDate : defaultDate);
          }}
        />
        {!cancelImmediately && (
          <Form.Group className="mb-0">
            <Form.Label>Cancellation date</Form.Label>
            <Form.Control
              type="date"
              min={minDate}
              value={cancellationDate}
              onChange={(e) => setCancellationDate(e.target.value)}
            />
          </Form.Group>
        )}
        {showPendingSaveNote && (
          <div className="small text-muted mt-2 mb-0">
            This change will not take effect until you save.
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <BsButton variant="outline-secondary" onClick={onHide}>
          Go Back
        </BsButton>
        <BsButton
          variant="danger"
          onClick={() => {
            if (cancellationDate) {
              onConfirm(cancellationDate);
            }
          }}
        >
          Confirm Cancellation
        </BsButton>
      </Modal.Footer>
    </Modal>
  );
}
